export interface ContractClause {
  id: string;
  title: string;
  content: string;
  category: 'legal' | 'technical' | 'payment' | 'content' | 'privacy' | 'obligations';
  required: boolean;
  planIds: string[];
}

export type ContractStatus = 'draft' | 'pending_signature' | 'signed' | 'expired' | 'cancelled' | 'needs_revision';

export interface SignatureEvidence {
  signedAt: string;
  signerIp: string;
  signerUserAgent: string;
  signedByName: string;
  signedByEmail: string;
  signedByIdentifier: string;
  writtenSignature: string;
}

export interface RadioContract {
  id: number;
  contractNumber: string;
  radioName: string;
  radioId: number;
  frequency: string;
  planId: string;
  planName: string;
  monthlyFee: number;
  annualLicense: number;
  storageIncluded: string;
  customDomain: string | null;
  status: ContractStatus;
  signedAt: string | null;
  signedByName: string | null;
  signedByEmail: string | null;
  signedByIdentifier: string | null;
  contractVersion: number;
  generatedAt: string;
  expiresAt: string | null;
  nextRenewal: string | null;
  clauses: string[];
  signedDocumentUrl: string | null;
  invoiceId: number | null;
  cancellationReason: string | null;
  revisionReason: string | null;
  signatureToken?: string;
  signatureLinkValid?: boolean;
  signatureEvidence?: SignatureEvidence | null;
}

export const defaultClauses: ContractClause[] = [
  {
    id: 'cl_001',
    title: '1. Partes del Contrato',
    content: 'El presente Contrato de Prestación de Servicios de Administración de Radio Online (en adelante, el "Contrato") se celebra entre RadioFrecuenciaWeb (en adelante, "RFW" o "El Proveedor"), sociedad debidamente constituida, y la Estación de Radio identificada en el registro (en adelante, "La Radio" o "El Cliente"), quienes acuerdan libre y voluntariamente las siguientes cláusulas. La aceptación del presente Contrato por parte de La Radio constituye un acuerdo vinculante entre ambas partes desde el momento de su firma electrónica. RFW es una plataforma de administración, gestión, promoción e interacción para emisoras de radio por internet. RFW NO vende ni provee servicios de streaming de audio, servidores SHOUTcast/Icecast, AutoDJ ni ancho de banda de transmisión. Cada emisora utiliza su propio proveedor externo de streaming y RFW reproduce su señal dentro de la Plataforma.',
    category: 'legal',
    required: true,
    planIds: [],
  },
  {
    id: 'cl_002',
    title: '2. Servicio de Administración y Gestión de Emisoras — Lo que SÍ y NO ofrece RadioFrecuenciaWeb',
    content: 'RadioFrecuenciaWeb es una plataforma de administración, gestión, promoción e interacción para emisoras de radio por internet.\n\nLO QUE RADIOFRECUENCIAWEB SÍ OFRECE:\n• Administración de radio online con herramientas de gestión.\n• Perfil público de emisora con identidad visual y marca.\n• Frecuencia RFW exclusiva dentro del directorio de la Plataforma.\n• Cabina interactiva para locutores con chat en vivo, mensajes y moderación.\n• Chat en tiempo real entre oyentes y la emisora durante las transmisiones.\n• Comunidad de oyentes con seguidores, interacciones y notificaciones.\n• Organización y publicación de eventos propios de la emisora.\n• Galería de fotos y videos con gestión de contenido multimedia.\n• Transmisiones en vivo (Lives) dentro de la Plataforma si el plan lo permite.\n• Retransmisión futura a redes sociales (funcionalidad en desarrollo).\n• Directorio oficial de emisoras con búsqueda por categoría, género y ubicación.\n• Aplicación móvil futura para oyentes (en desarrollo).\n\nLO QUE RADIOFRECUENCIAWEB NO OFRECE:\n• Servidor de streaming de audio. La Plataforma no provee infraestructura de transmisión de audio.\n• Servidores SHOUTcast o Icecast. No comercializamos ni administramos servidores de streaming de terceros.\n• AutoDJ. No ofrecemos software de automatización radial ni gestión de playlists automáticas.\n• Ancho de banda de señal de radio. No proveemos conectividad ni tráfico de datos para la transmisión de audio.\n• Licencias musicales. No gestionamos ni proveemos licencias de derechos de autor, regalías, ni permisos de reproducción musical.\n• Servicios de radiodifusión tradicional. No operamos como estación de radio ni como organismo de radiodifusión.\n\nCada emisora es responsable de contratar, mantener y administrar su propio proveedor de streaming o infraestructura de transmisión. RadioFrecuenciaWeb únicamente permite registrar, administrar, reproducir y presentar dentro de la plataforma la URL de transmisión proporcionada por la propia emisora. La disponibilidad, estabilidad, calidad de audio, continuidad de transmisión, consumo de ancho de banda y funcionamiento del servicio de streaming son responsabilidad exclusiva del proveedor de transmisión contratado por la emisora y/o de la propia emisora. Si la URL de stream falla, se cae, no transmite o el proveedor externo tiene problemas, RadioFrecuenciaWeb no es responsable por esa interrupción.\n\nAsimismo, la emisora es la única responsable de obtener, mantener y cumplir con todas las licencias, permisos, autorizaciones, regalías y obligaciones legales relacionadas con la música, programas, eventos deportivos, podcasts, retransmisiones y cualquier otro contenido difundido mediante su señal de audio. RadioFrecuenciaWeb actúa únicamente como plataforma tecnológica de administración y no asume responsabilidad sobre los contenidos transmitidos por las emisoras ni sobre los derechos de autor asociados a dichos contenidos. La plataforma podrá suspender o cancelar cuentas que incumplan la legislación aplicable, reciban reclamaciones válidas por infracción de derechos de autor o utilicen la plataforma para actividades no autorizadas.',
    category: 'legal',
    required: true,
    planIds: [],
  },
  {
    id: 'cl_003',
    title: '3. Duración y Vigencia',
    content: 'El Contrato tendrá una duración igual al período de facturación contratado por La Radio (mensual o anual). Al finalizar cada período, el Contrato se renovará automáticamente por un período idéntico, salvo que cualquiera de las partes notifique su voluntad de no renovarlo con al menos 15 (quince) días corridos de anticipación a la fecha de vencimiento. La vigencia efectiva de los servicios está condicionada al pago puntual de las tarifas correspondientes.',
    category: 'legal',
    required: true,
    planIds: [],
  },
  {
    id: 'cl_004',
    title: '4. Responsabilidad sobre el Contenido Emitido',
    content: 'LA RADIO ES LA ÚNICA Y EXCLUSIVA RESPONSABLE por todo el contenido que transmita a través de la plataforma de RFW. Esto incluye, sin limitación: música, locuciones, comentarios, entrevistas, anuncios publicitarios, y cualquier otro material sonoro, visual o textual difundido durante su programación. RFW NO ejerce control editorial previo sobre las transmisiones y NO se hace responsable por el contenido que La Radio emita, incluyendo —pero no limitado a— contenido ofensivo, difamatorio, obsceno, violento, discriminatorio, o que viole derechos de terceros. En particular, La Radio se obliga a cumplir estrictamente con las normativas de horario familiar o de protección al menor aplicables en su jurisdicción, absteniéndose de emitir contenido inapropiado durante las franjas horarias designadas como de protección infantil y familiar.',
    category: 'content',
    required: true,
    planIds: [],
  },
  {
    id: 'cl_005',
    title: '5. Obligaciones de La Radio respecto al Contenido',
    content: 'La Radio se compromete a: (a) No transmitir contenido que infrinja derechos de propiedad intelectual de terceros, incluyendo música, efectos de sonido, grabaciones, o cualquier material protegido sin la debida licencia o autorización. (b) No difundir contenido que promueva el odio, la violencia, la discriminación, la pornografía infantil, el terrorismo, o cualquier actividad ilegal. (c) Obtener todas las licencias, autorizaciones y permisos necesarios para la difusión del contenido que transmita, incluyendo —según corresponda— licencias de sociedades de gestión colectiva, derechos de autor, derechos conexos, y permisos de radiodifusión exigidos por las autoridades competentes de su país. (d) Mantener indemne a RFW frente a cualquier reclamo, demanda, sanción o perjuicio derivado del contenido que transmita.',
    category: 'content',
    required: true,
    planIds: [],
  },
  {
    id: 'cl_006',
    title: '6. Propiedad Intelectual de la Plataforma',
    content: 'Todos los derechos de propiedad intelectual sobre la plataforma RFW —incluyendo su código fuente, diseño, interfaz de usuario, bases de datos, logotipos, marcas, nombres comerciales, y documentación— son propiedad exclusiva de RFW o de sus licenciantes. La Radio NO adquiere ningún derecho de propiedad sobre la plataforma, sino únicamente un derecho limitado, revocable y no exclusivo de uso durante la vigencia del Contrato. La Radio se obliga a no copiar, modificar, descompilar, realizar ingeniería inversa, ni crear obras derivadas de la plataforma.',
    category: 'legal',
    required: true,
    planIds: [],
  },
  {
    id: 'cl_007',
    title: '7. Condición "Tal Cual" (AS IS) del Servicio',
    content: 'EL SERVICIO SE PROPORCIONA "TAL CUAL" ("AS IS") Y "SEGÚN DISPONIBILIDAD" ("AS AVAILABLE"), SIN GARANTÍAS DE NINGÚN TIPO. RFW NO GARANTIZA QUE EL SERVICIO SEA ININTERRUMPIDO, OPORTUNO, SEGURO O LIBRE DE ERRORES. La Radio reconoce y acepta que la plataforma puede presentar interrupciones, demoras, fallas técnicas, o limitaciones inherentes a la tecnología utilizada. RFW no asume responsabilidad por pérdidas de datos, interrupciones de transmisión, degradación de calidad de audio, o cualquier otro inconveniente técnico que pueda surgir durante el uso de la plataforma. La Radio acepta expresamente que utiliza la plataforma bajo su propio riesgo.',
    category: 'technical',
    required: true,
    planIds: [],
  },
  {
    id: 'cl_008',
    title: '8. Disponibilidad del Servicio y Dependencia de Terceros',
    content: 'RFW NO GARANTIZA disponibilidad continua, ininterrumpida o del 100% del servicio. La plataforma de RFW depende de proveedores de servicios externos que están fuera de nuestro control, incluyendo —pero no limitado a—: (a) Proveedores de hosting y servidores VPS que alojan la infraestructura. (b) Proveedores de servicios de Internet (ISP) de los cuales depende la conectividad tanto de RFW como de La Radio y sus oyentes. (c) Redes de distribución de contenido (CDN) utilizadas para la entrega de streams de audio y contenido multimedia. (d) Proveedores de infraestructura cloud (Supabase y servicios asociados) que gestionan la base de datos y metadatos. (e) Proveedores de DNS, certificados SSL, y demás servicios de red. RFW no será responsable por interrupciones, demoras, o fallas causadas por cualquiera de estos proveedores terceros, ni por eventos de fuerza mayor, caso fortuito, ataques cibernéticos (DDoS, hacking), fallas eléctricas, desastres naturales, o cualquier otra causa fuera del control razonable de RFW.',
    category: 'technical',
    required: true,
    planIds: [],
  },
  {
    id: 'cl_009',
    title: '9. Límites de Almacenamiento y Uso',
    content: 'La capacidad de almacenamiento asignada a La Radio está determinada por el plan contratado. La Radio se obliga a no exceder los límites de almacenamiento, cantidad de imágenes, videos, posts, y demás recursos especificados en su plan. RFW se reserva el derecho de: (a) Limitar la carga de nuevos contenidos cuando se alcance el límite de almacenamiento. (b) Notificar a La Radio sobre la necesidad de ampliar su plan para continuar utilizando los servicios de alojamiento. (c) Eliminar contenido que exceda los límites del plan si La Radio no regulariza su situación en un plazo de 30 días corridos desde la notificación. Las radio que no tengan plan de almacenamiento contratado no podrán subir archivos multimedia a la plataforma.',
    category: 'technical',
    required: true,
    planIds: [],
  },
  {
    id: 'cl_010',
    title: '10. Precio, Facturación y Pago',
    content: 'La Radio se obliga a abonar las tarifas correspondientes al plan contratado según los precios vigentes publicados en el sitio web de RFW al momento de la contratación o renovación. Los pagos se realizarán en la periodicidad elegida (mensual o anual) mediante los métodos de pago aceptados por la plataforma. La falta de pago en la fecha de vencimiento facultará a RFW a: (a) Suspender temporalmente los servicios hasta la regularización del pago. (b) Degradar la cuenta de La Radio a un plan gratuito limitado si el impago persiste por más de 15 días. (c) Dar por resuelto el Contrato y eliminar la cuenta de La Radio si el impago persiste por más de 45 días corridos, previa notificación fehaciente. RFW se reserva el derecho de modificar los precios de los planes, notificando a La Radio con al menos 30 días de anticipación a la fecha de renovación.',
    category: 'payment',
    required: true,
    planIds: [],
  },
  {
    id: 'cl_011',
    title: '11. Limitación de Responsabilidad',
    content: 'EN LA MÁXIMA MEDIDA PERMITIDA POR LA LEY APLICABLE, RFW NO SERÁ RESPONSABLE POR DAÑOS DIRECTOS, INDIRECTOS, INCIDENTALES, ESPECIALES, CONSECUENTES O PUNITIVOS, INCLUYENDO PERO NO LIMITADO A: PÉRDIDA DE INGRESOS, PÉRDIDA DE AUDIENCIA, PÉRDIDA DE DATOS, PÉRDIDA DE CONTENIDO MULTIMEDIA, INTERRUPCIÓN DE TRANSMISIONES, DAÑO A LA REPUTACIÓN, O CUALQUIER OTRO PERJUICIO COMERCIAL O PERSONAL DERIVADO DEL USO O IMPOSIBILIDAD DE USO DE LA PLATAFORMA, INCLUSO SI RFW HA SIDO ADVERTIDA DE LA POSIBILIDAD DE TALES DAÑOS. La responsabilidad total de RFW, por cualquier causa y bajo cualquier teoría legal, estará limitada al monto total de las tarifas abonadas por La Radio durante los 6 (seis) meses inmediatamente anteriores al hecho que originó el reclamo.',
    category: 'legal',
    required: true,
    planIds: [],
  },
  {
    id: 'cl_012',
    title: '12. Política de Privacidad y Protección de Datos',
    content: 'RFW se compromete a tratar los datos personales de La Radio, sus locutores, administradores y usuarios de conformidad con su Política de Privacidad publicada en el sitio web. La Radio, como responsable del tratamiento de los datos de sus oyentes a través de las funcionalidades de interacción (cabina de mensajes, chats, formularios), se obliga a cumplir con todas las leyes de protección de datos aplicables en su jurisdicción, incluyendo la obtención de los consentimientos necesarios y la provisión de mecanismos para el ejercicio de derechos ARCO (Acceso, Rectificación, Cancelación y Oposición) por parte de los titulares de los datos.',
    category: 'privacy',
    required: true,
    planIds: [],
  },
  {
    id: 'cl_013',
    title: '13. Términos de Servicio',
    content: 'La Radio declara haber leído, comprendido y aceptado en su totalidad los Términos de Servicio de RFW publicados en el sitio web, los cuales forman parte integrante del presente Contrato. Los Términos de Servicio podrán ser actualizados periódicamente por RFW. Las modificaciones sustanciales serán notificadas a La Radio con al menos 15 días de anticipación. El uso continuado de la plataforma después de la entrada en vigor de los nuevos términos implicará la aceptación tácita de los mismos.',
    category: 'legal',
    required: true,
    planIds: [],
  },
  {
    id: 'cl_014',
    title: '14. Terminación del Contrato',
    content: 'El Contrato podrá ser resuelto por cualquiera de las partes en los siguientes casos: (a) Por mutuo acuerdo expresado por escrito o medio electrónico verificable. (b) Por incumplimiento grave de cualquiera de las obligaciones establecidas en este Contrato, previa notificación a la parte incumplidora y otorgamiento de un plazo de 10 (diez) días para subsanar el incumplimiento, salvo que por su naturaleza sea insubsanable. (c) Por decisión unilateral de RFW en casos de violación de las cláusulas 4, 5 u 11 del presente Contrato, sin necesidad de preaviso. (d) Por disolución, quiebra o insolvencia de cualquiera de las partes. En todos los casos de terminación, La Radio será responsable del pago de las tarifas devengadas hasta la fecha efectiva de terminación.',
    category: 'legal',
    required: true,
    planIds: [],
  },
  {
    id: 'cl_015',
    title: '15. Modificaciones al Contrato por Cambio de Plan',
    content: 'En caso de que La Radio modifique su plan contratado (upgrade o downgrade), el presente Contrato se actualizará automáticamente para reflejar: (a) Las nuevas tarifas aplicables. (b) Los nuevos límites de almacenamiento y funcionalidades. (c) Las cláusulas específicas asociadas al nuevo plan. La Radio reconoce que el cambio de plan constituye una modificación contractual que requiere la aceptación expresa de los nuevos términos mediante firma electrónica del Contrato actualizado. Hasta que La Radio no firme el Contrato actualizado, los servicios correspondientes al nuevo plan no estarán plenamente habilitados.',
    category: 'payment',
    required: true,
    planIds: [],
  },
  {
    id: 'cl_016',
    title: '16. Ley Aplicable y Jurisdicción',
    content: 'El presente Contrato se regirá e interpretará de conformidad con las leyes de la República Argentina. Para cualquier controversia derivada del presente Contrato, las partes se someten a la jurisdicción de los Tribunales Ordinarios con competencia en materia comercial de la Ciudad Autónoma de Buenos Aires, República Argentina, renunciando expresamente a cualquier otro fuero o jurisdicción que pudiera corresponderles.',
    category: 'legal',
    required: true,
    planIds: [],
  },
  {
    id: 'cl_017',
    title: '17. Firma Electrónica y Validez Legal',
    content: 'Las partes reconocen y aceptan que la firma electrónica del presente Contrato tiene plena validez legal y fuerza ejecutiva, de conformidad con la legislación aplicable en materia de firma digital y comercio electrónico. La aceptación mediante clic ("click-wrap agreement") y la firma electrónica a través de la plataforma constituyen manifestación válida del consentimiento de La Radio. La Radio se obliga a conservar una copia del Contrato firmado para sus registros.',
    category: 'legal',
    required: true,
    planIds: [],
  },
  {
    id: 'cl_018',
    title: '18. Cláusula de Indemnidad Específica por Contenido en Horario Familiar',
    content: 'La Radio se obliga específicamente a NO emitir contenido ofensivo, obsceno, violento, sexualmente explícito, o que utilice lenguaje inapropiado durante el horario familiar comprendido entre las 06:00 y las 22:00 horas (hora local de la jurisdicción donde opera La Radio). La violación de esta cláusula será considerada un incumplimiento grave que facultará a RFW a suspender inmediatamente la transmisión y, en caso de reincidencia, resolver el Contrato sin derecho a reembolso. La Radio mantendrá indemne a RFW frente a cualquier sanción administrativa, multa, o reclamo de terceros —incluyendo padres, tutores, organismos de protección al menor, y autoridades de radiodifusión— derivados del incumplimiento de esta cláusula de horario protegido.',
    category: 'content',
    required: true,
    planIds: [],
  },
  {
    id: 'cl_019',
    title: '19. Suspensión y Reactivación de la Radio',
    content: 'La activación de La Radio en la plataforma está condicionada a: (a) La firma electrónica del presente Contrato por parte del representante autorizado de La Radio. (b) La verificación satisfactoria del stream de audio por parte del equipo técnico de RFW. (c) La verificación de que el contenido de prueba cumple con las políticas de contenido de RFW. Una vez activada, RFW se reserva el derecho de suspender la emisión de La Radio en caso de incumplimiento contractual. La reactivación requerirá la subsanación del incumplimiento y, si RFW lo considera necesario, la firma de un nuevo Contrato actualizado.',
    category: 'obligations',
    required: true,
    planIds: [],
  },
  {
    id: 'cl_020',
    title: '20. Aceptación Total y Declaración Jurada',
    content: 'La Radio, a través de su representante legal o persona debidamente autorizada, DECLARA BAJO JURAMENTO: (a) Que ha leído y comprendido la totalidad de las cláusulas del presente Contrato. (b) Que acepta sin reservas cada una de las obligaciones, limitaciones de responsabilidad, y condiciones aquí establecidas. (c) Que los datos proporcionados durante el registro (nombre de la radio, frecuencia, datos del titular, correo electrónico, etc.) son veraces, completos y actualizados. (d) Que cuenta con las autorizaciones, licencias y permisos legalmente exigidos para operar una estación de radio online en su jurisdicción. (e) Que conoce y acepta que RFW es una plataforma tecnológica que proporciona herramientas de administración, gestión, promoción e interacción para emisoras, que NO ofrece servicios de transmisión de audio (streaming), servidores SHOUTcast, Icecast ni AutoDJ, y que no actúa como licenciataria de contenidos ni como organismo de radiodifusión. LA FIRMA DEL PRESENTE CONTRATO IMPLICA LA ACEPTACIÓN TOTAL E INCONDICIONAL DE TODAS SUS CLÁUSULAS.',
    category: 'legal',
    required: true,
    planIds: [],
  },
  {
    id: 'cl_021',
    title: '21. Propiedad Intelectual del Contenido Transmitido',
    content: 'La Radio declara y garantiza que es titular o licenciataria autorizada de todos los derechos de propiedad intelectual —incluyendo pero no limitado a derechos de autor, derechos conexos, derechos de intérpretes, derechos fonográficos, y derechos de sincronización— sobre la totalidad del contenido que transmite a través de la plataforma de RFW. Esto comprende, sin excepción: obras musicales, composiciones, letras, grabaciones sonoras, locuciones, podcasts, jingles, cuñas publicitarias, efectos de sonido, imágenes, fotografías, videos, logotipos, marcas, y cualquier otro material protegido por las leyes de propiedad intelectual aplicables. La Radio es la única responsable de obtener, mantener vigentes y acreditar —frente a RFW o cualquier autoridad— todas las licencias, autorizaciones, permisos y pagos de regalías exigidos por las sociedades de gestión colectiva, titulares de derechos, editoras musicales, sellos discográficos y cualquier otro tercero con derechos sobre el contenido transmitido. RFW NO se hace responsable, en ningún caso y bajo ninguna circunstancia, por la violación de derechos de propiedad intelectual cometida por La Radio. La Radio mantendrá indemne a RFW frente a cualquier reclamo, demanda, sanción administrativa, multa, medida cautelar, o condena judicial derivada de la infracción de derechos de propiedad intelectual en el contenido que La Radio transmita.',
    category: 'content',
    required: true,
    planIds: [],
  },
  {
    id: 'cl_022',
    title: '22. Responsabilidad Exclusiva por Derechos de Autor y Permisos de Reproducción Musical',
    content: 'LA RADIO ASUME EN FORMA EXCLUSIVA, TOTAL E IRRESTRICTA TODA LA RESPONSABILIDAD POR LOS DERECHOS DE AUTOR, DERECHOS CONEXOS, DERECHOS FONOGRÁFICOS Y PERMISOS DE REPRODUCCIÓN DE OBRAS MUSICALES Y FONOGRAMAS QUE TRANSMITA A TRAVÉS DE LA PLATAFORMA. Es obligación exclusiva de La Radio: (a) Obtener de las sociedades de gestión colectiva correspondientes (tales como SADAIC, AADI-CAPIF, ASCAP, BMI, SESAC, PRS, GEMA, SACEM, etc.) las licencias necesarias para la comunicación pública, ejecución y reproducción de obras musicales. (b) Obtener de los titulares de derechos fonográficos y editoriales las autorizaciones requeridas para la transmisión digital (webcasting, streaming, simulcasting) de fonogramas. (c) Abonar puntualmente todas las regalías, aranceles, cánones y compensaciones establecidas por las entidades de gestión colectiva y los titulares de derechos. (d) Conservar y mantener a disposición de RFW —si ésta lo requiriera— toda la documentación que acredite el cumplimiento de las obligaciones precedentes. RFW no actúa como organismo de radiodifusión, no es titular de licencias de comunicación pública de obras musicales, no representa a ninguna sociedad de gestión colectiva, y NO se involucra, bajo ningún concepto, en la gestión, obtención, verificación o pago de derechos de autor, derechos conexos, permisos de reproducción musical, ni regalías de ningún tipo. RFW es una plataforma tecnológica que proporciona exclusivamente herramientas de administración, gestión, promoción e interacción para emisoras. RFW NO vende ni provee streaming de audio, servidores SHOUTcast/Icecast, AutoDJ ni ancho de banda de transmisión. Todo el contenido —incluyendo la selección musical, programación, playlists, y curaduría— es responsabilidad única y exclusiva de La Radio.',
    category: 'content',
    required: true,
    planIds: [],
  },
  {
    id: 'cl_023',
    title: '23. Licencia de Uso de Frecuencia RFW',
    content: 'La asignación de una frecuencia RFW a La Radio dentro del directorio de RadioFrecuenciaWeb está sujeta al pago de la Licencia Anual de Frecuencia RFW vigente al momento de la contratación. Esta licencia es independiente del plan mensual contratado y cubre: (a) La reserva exclusiva de la frecuencia RFW asignada a La Radio dentro del ecosistema RFW. (b) El derecho de uso de la frecuencia dentro de la plataforma y el dial digital. (c) La inclusión y permanencia en el directorio oficial de emisoras de RFW. (d) El mantenimiento de la página pública de perfil de La Radio. (e) La conservación de seguidores, comunidad, estadísticas e historial. (f) El acceso a actualizaciones de la plataforma y nuevas funcionalidades incorporadas durante la vigencia. (g) El acceso a futuras mejoras de la aplicación móvil. (h) El mantenimiento operativo de la infraestructura. (i) El soporte técnico básico según el plan contratado. (j) La seguridad y monitoreo de la plataforma. La falta de pago de la Licencia Anual de Frecuencia RFW dentro del período de gracia configurado por RFW podrá ocasionar: suspensión temporal de la frecuencia, restricción de acceso a funciones premium, y eventual pérdida de la reserva exclusiva con liberación de la frecuencia para futura asignación a terceros. Los términos específicos de la licencia —incluyendo monto, vigencia y condiciones— se encuentran publicados en la sección de Tarifas de Plataforma y podrán ser modificados por RFW notificando con al menos 30 días de anticipación.',
    category: 'payment',
    required: true,
    planIds: [],
  },
  {
    id: 'cl_024',
    title: '24. Derechos de Autor y Licencias Musicales',
    content: 'RadioFrecuenciaWeb proporciona únicamente la infraestructura tecnológica para que las emisoras transmitan contenido a través de internet. Cada estación de radio es la única responsable de obtener, mantener y cumplir con todas las licencias, permisos, autorizaciones, regalías y obligaciones legales requeridas para la reproducción, comunicación pública, retransmisión o distribución de música, audio, programas, podcasts u otros contenidos protegidos por derechos de autor. RadioFrecuenciaWeb no verifica ni garantiza que una emisora cuente con las licencias necesarias para transmitir contenido protegido. La responsabilidad por reclamaciones, infracciones, multas, sanciones, pagos de regalías o disputas relacionadas con derechos de autor corresponde exclusivamente a la emisora propietaria de la cuenta. RadioFrecuenciaWeb podrá suspender o cancelar cualquier cuenta que reciba reportes válidos de infracción de derechos de autor, uso indebido de contenido protegido o incumplimiento de la legislación aplicable. Al utilizar la plataforma, la emisora declara y garantiza que posee todos los derechos, permisos y autorizaciones necesarios para transmitir el contenido que publique o distribuya a través de RadioFrecuenciaWeb.',
    category: 'content',
    required: true,
    planIds: [],
  },
];

export const planSpecificClauses: ContractClause[] = [
  {
    id: 'cl_101',
    title: 'Cláusula Especial Plan Free — Limitaciones del Servicio Gratuito',
    content: 'El Plan Free (Starter) es un servicio gratuito con funcionalidades limitadas. La Radio en Plan Free acepta que: (a) No tiene derecho a almacenamiento de archivos multimedia (imágenes, videos, fotos). (b) El servicio puede incluir publicidad de RFW insertada en la página de perfil de la radio. (c) RFW podrá limitar el ancho de banda del stream en momentos de alta demanda del sistema. (d) El soporte técnico se limita a documentación en línea y foros comunitarios, sin atención personalizada. (e) RFW se reserva el derecho de modificar, suspender o discontinuar el Plan Free en cualquier momento, notificando con 30 días de anticipación.',
    category: 'payment',
    required: true,
    planIds: ['plan_001'],
  },
  {
    id: 'cl_102',
    title: 'Cláusula Especial Plan Básico — Condiciones de Almacenamiento',
    content: 'La Radio en Plan Básico dispone de 5 GB de almacenamiento para fotos y archivos multimedia. El servicio de cabina de mensajes está limitado a 100 mensajes simultáneos. Los conectores sociales están habilitados para hasta 3 plataformas (Facebook, Twitter/X, Instagram). RFW se reserva el derecho de aplicar throttling de ancho de banda si el consumo de recursos de La Radio afecta el rendimiento general del sistema.',
    category: 'payment',
    required: true,
    planIds: ['plan_002'],
  },
  {
    id: 'cl_103',
    title: 'Cláusula Especial Plan Pro — Funcionalidades y Límites',
    content: 'La Radio en Plan Pro dispone de 50 GB de almacenamiento para contenido multimedia, incluyendo fotos, videos y galerías. Se habilitan hasta 15 eventos por mes calendario. La cabina de mensajes soporta hasta 500 mensajes simultáneos. Los conectores sociales están habilitados para todas las plataformas disponibles. La Radio acepta que los excesos de almacenamiento por encima del límite generarán cargos adicionales según las tarifas vigentes.',
    category: 'payment',
    required: true,
    planIds: ['plan_003'],
  },
  {
    id: 'cl_104',
    title: 'Cláusula Especial Plan Business — SLA y Compromisos',
    content: 'La Radio en Plan Business dispone de 100 GB de almacenamiento, App Oficial personalizada, sistema de moderación avanzada y analytics. RFW se compromete a mantener una disponibilidad del servicio del 99.5% mensual (excluyendo mantenimientos programados notificados con 48 horas de anticipación). En caso de incumplimiento del SLA, La Radio tendrá derecho a un crédito equivalente al 5% de la tarifa mensual por cada 1% de disponibilidad por debajo del umbral, hasta un máximo del 30% de la tarifa mensual.',
    category: 'payment',
    required: true,
    planIds: ['plan_004'],
  },
  {
    id: 'cl_105',
    title: 'Cláusula Especial Plan Premium — Términos Avanzados',
    content: 'La Radio en Plan Premium dispone de 500 GB de almacenamiento, API completa, soporte 24/7 prioritario, y frecuencias premium. RFW se compromete a una disponibilidad del 99.9% mensual. El soporte 24/7 incluye respuesta en menos de 2 horas para incidencias críticas. Se asigna un Ejecutivo de Cuenta dedicado. La API permite integraciones personalizadas bajo los términos de uso de API publicados separadamente. La Radio Premium tiene prioridad en la asignación de recursos de CDN y ancho de banda.',
    category: 'payment',
    required: true,
    planIds: ['plan_005'],
  },
  {
    id: 'cl_106',
    title: 'Cláusula Especial Plan Enterprise — Términos Personalizados',
    content: 'La Radio en Plan Enterprise dispone de 1 TB de almacenamiento, SLA garantizado del 99.95%, API dedicada, y onboarding personalizado. Los términos específicos del Plan Enterprise, incluyendo precios por volumen, períodos de compromiso, y condiciones de salida, se detallan en un Anexo al presente Contrato que forma parte integrante del mismo. El incumplimiento del SLA del 99.95% generará créditos equivalentes al 10% de la tarifa mensual por cada 0.1% por debajo del umbral.',
    category: 'payment',
    required: true,
    planIds: ['plan_006'],
  },
];

export function getAllClauses(): ContractClause[] {
  return [...defaultClauses, ...planSpecificClauses];
}

export function getClausesByPlanId(planId: string): ContractClause[] {
  const defaults = defaultClauses;
  const specifics = planSpecificClauses.filter(
    (c) => c.planIds.includes(planId) || c.planIds.length === 0,
  );
  return [...defaults, ...specifics];
}

export const radioContracts: RadioContract[] = [
  {
    id: 1,
    contractNumber: 'CON-2025-0001',
    radioName: 'Radio Nueva Actitud',
    radioId: 101,
    frequency: '87.90 RFW',
    planId: 'plan_005',
    planName: 'Premium',
    monthlyFee: 79,
    annualLicense: 49.95,
    storageIncluded: '500 GB',
    customDomain: 'nuevaactitud.fm',
    status: 'signed',
    signedAt: '2025-03-15T10:30:00Z',
    signedByName: 'Carlos Mendoza',
    signedByEmail: 'carlos@nuevaactitud.com',
    signedByIdentifier: 'DNI 28.456.789',
    contractVersion: 2,
    generatedAt: '2025-03-15T08:00:00Z',
    expiresAt: '2027-03-15T08:00:00Z',
    nextRenewal: '2027-03-15',
    clauses: ['cl_001', 'cl_002', 'cl_003', 'cl_004', 'cl_005', 'cl_006', 'cl_007', 'cl_008', 'cl_009', 'cl_010', 'cl_011', 'cl_012', 'cl_013', 'cl_014', 'cl_015', 'cl_016', 'cl_017', 'cl_018', 'cl_019', 'cl_020', 'cl_021', 'cl_022', 'cl_023', 'cl_024', 'cl_105'],
    signedDocumentUrl: null,
    invoiceId: 5001,
    cancellationReason: null,
    revisionReason: null,
  },
  {
    id: 2,
    contractNumber: 'CON-2025-0002',
    radioName: 'Stereo Visión',
    radioId: 102,
    frequency: '94.70 RFW',
    planId: 'plan_005',
    planName: 'Premium',
    monthlyFee: 79,
    annualLicense: 49.95,
    storageIncluded: '500 GB',
    customDomain: 'stereovision.fm',
    status: 'signed',
    signedAt: '2025-01-10T14:15:00Z',
    signedByName: 'María González',
    signedByEmail: 'maria@stereovision.fm',
    signedByIdentifier: 'DNI 32.123.456',
    contractVersion: 1,
    generatedAt: '2025-01-10T08:00:00Z',
    expiresAt: '2027-01-10T08:00:00Z',
    nextRenewal: '2027-01-10',
    clauses: ['cl_001', 'cl_002', 'cl_003', 'cl_004', 'cl_005', 'cl_006', 'cl_007', 'cl_008', 'cl_009', 'cl_010', 'cl_011', 'cl_012', 'cl_013', 'cl_014', 'cl_015', 'cl_016', 'cl_017', 'cl_018', 'cl_019', 'cl_020', 'cl_021', 'cl_022', 'cl_023', 'cl_024', 'cl_105'],
    signedDocumentUrl: null,
    invoiceId: 5002,
    cancellationReason: null,
    revisionReason: null,
  },
  {
    id: 3,
    contractNumber: 'CON-2025-0003',
    radioName: 'TechBeats',
    radioId: 103,
    frequency: '88.50 RFW',
    planId: 'plan_003',
    planName: 'Pro',
    monthlyFee: 19,
    annualLicense: 49.95,
    storageIncluded: '50 GB',
    customDomain: null,
    status: 'signed',
    signedAt: '2025-06-20T09:45:00Z',
    signedByName: 'Diego Ramírez',
    signedByEmail: 'diego@techbeats.io',
    signedByIdentifier: 'DNI 35.678.901',
    contractVersion: 1,
    generatedAt: '2025-06-20T08:00:00Z',
    expiresAt: '2026-06-20T08:00:00Z',
    nextRenewal: '2026-06-20',
    clauses: ['cl_001', 'cl_002', 'cl_003', 'cl_004', 'cl_005', 'cl_006', 'cl_007', 'cl_008', 'cl_009', 'cl_010', 'cl_011', 'cl_012', 'cl_013', 'cl_014', 'cl_015', 'cl_016', 'cl_017', 'cl_018', 'cl_019', 'cl_020', 'cl_021', 'cl_022', 'cl_023', 'cl_024', 'cl_103'],
    signedDocumentUrl: null,
    invoiceId: 5003,
    cancellationReason: null,
    revisionReason: null,
  },
  {
    id: 4,
    contractNumber: 'CON-2025-0004',
    radioName: 'Radio Élite Digital',
    radioId: 104,
    frequency: '91.30 RFW',
    planId: 'plan_003',
    planName: 'Pro',
    monthlyFee: 19,
    annualLicense: 49.95,
    storageIncluded: '50 GB',
    customDomain: null,
    status: 'needs_revision',
    signedAt: '2025-08-01T11:00:00Z',
    signedByName: 'Laura Castillo',
    signedByEmail: 'laura@elitedigital.fm',
    signedByIdentifier: 'DNI 29.876.543',
    contractVersion: 1,
    generatedAt: '2025-08-01T08:00:00Z',
    expiresAt: '2026-08-01T08:00:00Z',
    nextRenewal: '2026-08-01',
    clauses: ['cl_001', 'cl_002', 'cl_003', 'cl_004', 'cl_005', 'cl_006', 'cl_007', 'cl_008', 'cl_009', 'cl_010', 'cl_011', 'cl_012', 'cl_013', 'cl_014', 'cl_015', 'cl_016', 'cl_017', 'cl_018', 'cl_019', 'cl_020', 'cl_021', 'cl_022', 'cl_023', 'cl_024', 'cl_103'],
    signedDocumentUrl: null,
    invoiceId: 5004,
    cancellationReason: null,
    revisionReason: 'Cambio de cláusulas por actualización de plan. Se requiere nueva firma del representante legal.',
  },
  {
    id: 5,
    contractNumber: 'CON-2025-0005',
    radioName: 'Radio Urbana 101',
    radioId: 105,
    frequency: '103.50 RFW',
    planId: 'plan_005',
    planName: 'Premium',
    monthlyFee: 79,
    annualLicense: 49.95,
    storageIncluded: '500 GB',
    customDomain: 'urbana101.fm',
    status: 'signed',
    signedAt: '2025-04-01T15:20:00Z',
    signedByName: 'Omar Hernández',
    signedByEmail: 'omar@urbana101.fm',
    signedByIdentifier: 'DNI 31.234.567',
    contractVersion: 1,
    generatedAt: '2025-04-01T08:00:00Z',
    expiresAt: '2027-04-01T08:00:00Z',
    nextRenewal: '2027-04-01',
    clauses: ['cl_001', 'cl_002', 'cl_003', 'cl_004', 'cl_005', 'cl_006', 'cl_007', 'cl_008', 'cl_009', 'cl_010', 'cl_011', 'cl_012', 'cl_013', 'cl_014', 'cl_015', 'cl_016', 'cl_017', 'cl_018', 'cl_019', 'cl_020', 'cl_105'],
    signedDocumentUrl: null,
    invoiceId: 5005,
    cancellationReason: null,
    revisionReason: null,
  },
  {
    id: 6,
    contractNumber: 'CON-2026-0006',
    radioName: 'Radio Sonora Digital',
    radioId: 106,
    frequency: '96.20 RFW',
    planId: 'plan_001',
    planName: 'Starter',
    monthlyFee: 0,
    annualLicense: 0,
    storageIncluded: '0 GB',
    customDomain: null,
    status: 'pending_signature',
    signedAt: null,
    signedByName: null,
    signedByEmail: null,
    signedByIdentifier: null,
    contractVersion: 1,
    generatedAt: '2026-06-01T08:00:00Z',
    expiresAt: null,
    nextRenewal: null,
    clauses: ['cl_001', 'cl_002', 'cl_003', 'cl_004', 'cl_005', 'cl_006', 'cl_007', 'cl_008', 'cl_009', 'cl_010', 'cl_011', 'cl_012', 'cl_013', 'cl_014', 'cl_015', 'cl_016', 'cl_017', 'cl_018', 'cl_019', 'cl_020', 'cl_021', 'cl_022', 'cl_023', 'cl_024', 'cl_101'],
    signedDocumentUrl: null,
    invoiceId: null,
    cancellationReason: null,
    revisionReason: null,
  },
  {
    id: 7,
    contractNumber: 'CON-2026-0007',
    radioName: 'Rock Total Radio',
    radioId: 107,
    frequency: '87.01 RFW',
    planId: 'plan_005',
    planName: 'Premium',
    monthlyFee: 79,
    annualLicense: 49.95,
    storageIncluded: '500 GB',
    customDomain: 'rocktotal.fm',
    status: 'signed',
    signedAt: '2026-04-10T12:00:00Z',
    signedByName: 'Valentina Ríos',
    signedByEmail: 'valentina@rocktotal.fm',
    signedByIdentifier: 'DNI 33.456.789',
    contractVersion: 1,
    generatedAt: '2026-04-10T08:00:00Z',
    expiresAt: '2028-04-10T08:00:00Z',
    nextRenewal: '2028-04-10',
    clauses: ['cl_001', 'cl_002', 'cl_003', 'cl_004', 'cl_005', 'cl_006', 'cl_007', 'cl_008', 'cl_009', 'cl_010', 'cl_011', 'cl_012', 'cl_013', 'cl_014', 'cl_015', 'cl_016', 'cl_017', 'cl_018', 'cl_019', 'cl_020', 'cl_105'],
    signedDocumentUrl: null,
    invoiceId: 5007,
    cancellationReason: null,
    revisionReason: null,
  },
  {
    id: 8,
    contractNumber: 'CON-2026-0008',
    radioName: 'Radio Comunitaria Unida',
    radioId: 108,
    frequency: '87.02 RFW',
    planId: 'plan_001',
    planName: 'Starter',
    monthlyFee: 0,
    annualLicense: 0,
    storageIncluded: '0 GB',
    customDomain: null,
    status: 'draft',
    signedAt: null,
    signedByName: null,
    signedByEmail: null,
    signedByIdentifier: null,
    contractVersion: 1,
    generatedAt: '2026-06-10T08:00:00Z',
    expiresAt: null,
    nextRenewal: null,
    clauses: ['cl_001', 'cl_002', 'cl_003', 'cl_004', 'cl_005', 'cl_006', 'cl_007', 'cl_008', 'cl_009', 'cl_010', 'cl_011', 'cl_012', 'cl_013', 'cl_014', 'cl_015', 'cl_016', 'cl_017', 'cl_018', 'cl_019', 'cl_020', 'cl_021', 'cl_022', 'cl_023', 'cl_024', 'cl_101'],
    signedDocumentUrl: null,
    invoiceId: null,
    cancellationReason: null,
    revisionReason: null,
  },
  {
    id: 9,
    contractNumber: 'CON-2025-0009',
    radioName: 'Deportes Total Radio',
    radioId: 109,
    frequency: '120.00 RFW',
    planId: 'plan_005',
    planName: 'Premium',
    monthlyFee: 79,
    annualLicense: 49.95,
    storageIncluded: '500 GB',
    customDomain: 'deportestotal.fm',
    status: 'expired',
    signedAt: '2025-06-15T09:00:00Z',
    signedByName: 'Andrés Martínez',
    signedByEmail: 'andres@deportestotal.fm',
    signedByIdentifier: 'DNI 27.890.123',
    contractVersion: 1,
    generatedAt: '2025-06-15T08:00:00Z',
    expiresAt: '2026-06-15T08:00:00Z',
    nextRenewal: null,
    clauses: ['cl_001', 'cl_002', 'cl_003', 'cl_004', 'cl_005', 'cl_006', 'cl_007', 'cl_008', 'cl_009', 'cl_010', 'cl_011', 'cl_012', 'cl_013', 'cl_014', 'cl_015', 'cl_016', 'cl_017', 'cl_018', 'cl_019', 'cl_020', 'cl_105'],
    signedDocumentUrl: null,
    invoiceId: 5009,
    cancellationReason: null,
    revisionReason: null,
  },
  {
    id: 10,
    contractNumber: 'CON-2026-0010',
    radioName: 'Radio Salsa Brava',
    radioId: 110,
    frequency: '110.70 RFW',
    planId: 'plan_003',
    planName: 'Pro',
    monthlyFee: 19,
    annualLicense: 49.95,
    storageIncluded: '50 GB',
    customDomain: null,
    status: 'pending_signature',
    signedAt: null,
    signedByName: null,
    signedByEmail: null,
    signedByIdentifier: null,
    contractVersion: 1,
    generatedAt: '2026-06-13T08:00:00Z',
    expiresAt: null,
    nextRenewal: null,
    clauses: ['cl_001', 'cl_002', 'cl_003', 'cl_004', 'cl_005', 'cl_006', 'cl_007', 'cl_008', 'cl_009', 'cl_010', 'cl_011', 'cl_012', 'cl_013', 'cl_014', 'cl_015', 'cl_016', 'cl_017', 'cl_018', 'cl_019', 'cl_020', 'cl_021', 'cl_022', 'cl_023', 'cl_024', 'cl_103'],
    signedDocumentUrl: null,
    invoiceId: null,
    cancellationReason: null,
    revisionReason: null,
  },
  {
    id: 11,
    contractNumber: 'CON-2026-0011',
    radioName: 'Radio Hip Hop Nation',
    radioId: 111,
    frequency: '170.40 RFW',
    planId: 'plan_005',
    planName: 'Premium',
    monthlyFee: 79,
    annualLicense: 49.95,
    storageIncluded: '500 GB',
    customDomain: 'hiphopnation.fm',
    status: 'cancelled',
    signedAt: '2025-11-01T10:00:00Z',
    signedByName: 'Marcus Lee',
    signedByEmail: 'marcus@hiphopnation.fm',
    signedByIdentifier: 'DNI 94.567.890',
    contractVersion: 1,
    generatedAt: '2025-11-01T08:00:00Z',
    expiresAt: '2026-11-01T08:00:00Z',
    nextRenewal: null,
    clauses: ['cl_001', 'cl_002', 'cl_003', 'cl_004', 'cl_005', 'cl_006', 'cl_007', 'cl_008', 'cl_009', 'cl_010', 'cl_011', 'cl_012', 'cl_013', 'cl_014', 'cl_015', 'cl_016', 'cl_017', 'cl_018', 'cl_019', 'cl_020', 'cl_105'],
    signedDocumentUrl: null,
    invoiceId: 5011,
    cancellationReason: 'Cierre definitivo de la emisora. Incumplimiento reiterado de políticas de contenido.',
    revisionReason: null,
  },
  {
    id: 12,
    contractNumber: 'CON-2025-0012',
    radioName: 'Electro Vibes FM',
    radioId: 112,
    frequency: '96.50 RFW',
    planId: 'plan_003',
    planName: 'Pro',
    monthlyFee: 19,
    annualLicense: 49.95,
    storageIncluded: '50 GB',
    customDomain: null,
    status: 'signed',
    signedAt: '2025-04-20T16:30:00Z',
    signedByName: 'Lucas Oliveira',
    signedByEmail: 'lucas@electrovibes.fm',
    signedByIdentifier: 'DNI 40.123.456',
    contractVersion: 2,
    generatedAt: '2025-04-20T08:00:00Z',
    expiresAt: '2027-04-20T08:00:00Z',
    nextRenewal: '2027-04-20',
    clauses: ['cl_001', 'cl_002', 'cl_003', 'cl_004', 'cl_005', 'cl_006', 'cl_007', 'cl_008', 'cl_009', 'cl_010', 'cl_011', 'cl_012', 'cl_013', 'cl_014', 'cl_015', 'cl_016', 'cl_017', 'cl_018', 'cl_019', 'cl_020', 'cl_021', 'cl_022', 'cl_023', 'cl_024', 'cl_103'],
    signedDocumentUrl: null,
    invoiceId: 5012,
    cancellationReason: null,
    revisionReason: null,
  },
  {
    id: 13,
    contractNumber: 'CON-2026-0013',
    radioName: 'Clásica Mundial Radio',
    radioId: 113,
    frequency: '130.50 RFW',
    planId: 'plan_005',
    planName: 'Premium',
    monthlyFee: 79,
    annualLicense: 49.95,
    storageIncluded: '500 GB',
    customDomain: 'clasicamundial.fm',
    status: 'needs_revision',
    signedAt: '2026-02-28T11:00:00Z',
    signedByName: 'Antonio Vivaldi Jr.',
    signedByEmail: 'antonio@clasicamundial.fm',
    signedByIdentifier: 'DNI 38.901.234',
    contractVersion: 1,
    generatedAt: '2026-02-28T08:00:00Z',
    expiresAt: '2028-02-28T08:00:00Z',
    nextRenewal: '2028-02-28',
    clauses: ['cl_001', 'cl_002', 'cl_003', 'cl_004', 'cl_005', 'cl_006', 'cl_007', 'cl_008', 'cl_009', 'cl_010', 'cl_011', 'cl_012', 'cl_013', 'cl_014', 'cl_015', 'cl_016', 'cl_017', 'cl_018', 'cl_019', 'cl_020', 'cl_105'],
    signedDocumentUrl: null,
    invoiceId: 5013,
    cancellationReason: null,
    revisionReason: 'Se actualizaron las cláusulas de licencia anual. Requiere nueva firma del representante.',
  },
  {
    id: 14,
    contractNumber: 'CON-2026-0014',
    radioName: 'Radio Jazz Nocturno',
    radioId: 114,
    frequency: '91.30 RFW',
    planId: 'plan_003',
    planName: 'Pro',
    monthlyFee: 19,
    annualLicense: 49.95,
    storageIncluded: '50 GB',
    customDomain: null,
    status: 'signed',
    signedAt: '2026-04-05T10:15:00Z',
    signedByName: 'Ella Fitzgerald III',
    signedByEmail: 'ella@jazznocturno.fm',
    signedByIdentifier: 'DNI 36.789.012',
    contractVersion: 1,
    generatedAt: '2026-04-05T08:00:00Z',
    expiresAt: '2028-04-05T08:00:00Z',
    nextRenewal: '2028-04-05',
    clauses: ['cl_001', 'cl_002', 'cl_003', 'cl_004', 'cl_005', 'cl_006', 'cl_007', 'cl_008', 'cl_009', 'cl_010', 'cl_011', 'cl_012', 'cl_013', 'cl_014', 'cl_015', 'cl_016', 'cl_017', 'cl_018', 'cl_019', 'cl_020', 'cl_021', 'cl_022', 'cl_023', 'cl_024', 'cl_103'],
    signedDocumentUrl: null,
    invoiceId: 5014,
    cancellationReason: null,
    revisionReason: null,
  },
  {
    id: 15,
    contractNumber: 'CON-2026-0015',
    radioName: 'Radio Folclor Andino',
    radioId: 115,
    frequency: '107.90 RFW',
    planId: 'plan_002',
    planName: 'Básico',
    monthlyFee: 9,
    annualLicense: 49.95,
    storageIncluded: '5 GB',
    customDomain: null,
    status: 'signed',
    signedAt: '2026-01-05T08:45:00Z',
    signedByName: 'Juan Quispe',
    signedByEmail: 'juan@folclorandino.fm',
    signedByIdentifier: 'DNI 34.567.890',
    contractVersion: 1,
    generatedAt: '2026-01-05T08:00:00Z',
    expiresAt: '2027-01-05T08:00:00Z',
    nextRenewal: '2027-01-05',
    clauses: ['cl_001', 'cl_002', 'cl_003', 'cl_004', 'cl_005', 'cl_006', 'cl_007', 'cl_008', 'cl_009', 'cl_010', 'cl_011', 'cl_012', 'cl_013', 'cl_014', 'cl_015', 'cl_016', 'cl_017', 'cl_018', 'cl_019', 'cl_020', 'cl_021', 'cl_022', 'cl_023', 'cl_024', 'cl_102'],
    signedDocumentUrl: null,
    invoiceId: 5015,
    cancellationReason: null,
    revisionReason: null,
  },
  {
    id: 16,
    contractNumber: 'CON-2026-0016',
    radioName: 'Radio Country Road',
    radioId: 116,
    frequency: '190.20 RFW',
    planId: 'plan_001',
    planName: 'Starter',
    monthlyFee: 0,
    annualLicense: 0,
    storageIncluded: '0 GB',
    customDomain: null,
    status: 'draft',
    signedAt: null,
    signedByName: null,
    signedByEmail: null,
    signedByIdentifier: null,
    contractVersion: 1,
    generatedAt: '2026-06-08T08:00:00Z',
    expiresAt: null,
    nextRenewal: null,
    clauses: ['cl_001', 'cl_002', 'cl_003', 'cl_004', 'cl_005', 'cl_006', 'cl_007', 'cl_008', 'cl_009', 'cl_010', 'cl_011', 'cl_012', 'cl_013', 'cl_014', 'cl_015', 'cl_016', 'cl_017', 'cl_018', 'cl_019', 'cl_020', 'cl_021', 'cl_022', 'cl_023', 'cl_024', 'cl_101'],
    signedDocumentUrl: null,
    invoiceId: null,
    cancellationReason: null,
    revisionReason: null,
  },
];

export function getContractStatusLabel(status: ContractStatus): string {
  const map: Record<ContractStatus, string> = {
    draft: 'Borrador',
    pending_signature: 'Pendiente de Firma',
    signed: 'Firmado',
    expired: 'Expirado',
    cancelled: 'Cancelado',
    needs_revision: 'Necesita Revisión',
  };
  return map[status];
}

export function getContractStatusColor(status: ContractStatus): string {
  const map: Record<ContractStatus, string> = {
    draft: 'text-foreground-500 bg-background-200',
    pending_signature: 'text-amber-600 bg-amber-50',
    signed: 'text-emerald-600 bg-emerald-50',
    expired: 'text-red-600 bg-red-50',
    cancelled: 'text-stone-600 bg-stone-100',
    needs_revision: 'text-orange-600 bg-orange-50',
  };
  return map[status];
}

export function getContractStatusIcon(status: ContractStatus): string {
  const map: Record<ContractStatus, string> = {
    draft: 'ri-draft-line',
    pending_signature: 'ri-quill-pen-line',
    signed: 'ri-check-double-line',
    expired: 'ri-error-warning-line',
    cancelled: 'ri-close-circle-line',
    needs_revision: 'ri-loop-left-line',
  };
  return map[status];
}

export function getContractByToken(token: string): RadioContract | undefined {
  const tokenToId: Record<string, number> = {
    'sig-a7f3b2c1d4e5f6a8b9c0d1e2f3a4b5c6': 1,
    'sig-b8c4d3e5f6a7b9c0d1e2f3a4b5c6d7e8': 2,
    'sig-c9d5e4f6a7b8c0d1e2f3a4b5c6d7e8f9': 3,
    'sig-d0e6f5a7b8c9d1e2f3a4b5c6d7e8f9a0': 4,
    'sig-e1f7a6b8c9d0e2f3a4b5c6d7e8f9a0b1': 5,
    'sig-f2a8b7c9d0e1f3a4b5c6d7e8f9a0b1c2': 6,
    'sig-a3b9c8d0e1f2a4b5c6d7e8f9a0b1c2d3': 7,
    'sig-b4c0d9e1f2a3b5c6d7e8f9a0b1c2d3e4': 8,
    'sig-c5d1e0f2a3b4c6d7e8f9a0b1c2d3e4f5': 9,
    'sig-d6e2f1a3b4c5d7e8f9a0b1c2d3e4f5a6': 10,
    'sig-e7f3a2b4c5d6e8f9a0b1c2d3e4f5a6b7': 11,
    'sig-f8a4b3c5d6e7f9a0b1c2d3e4f5a6b7c8': 12,
    'sig-a9b5c4d6e7f8a0b1c2d3e4f5a6b7c8d9': 13,
    'sig-b0c6d5e7f8a9b1c2d3e4f5a6b7c8d9e0': 14,
    'sig-c1d7e6f8a9b0c2d3e4f5a6b7c8d9e0f1': 15,
    'sig-d2e8f7a9b0c1d3e4f5a6b7c8d9e0f1a2': 16,
  };
  const id = tokenToId[token];
  if (!id) return undefined;
  const contract = radioContracts.find((c) => c.id === id);
  if (!contract) return undefined;
  return {
    ...contract,
    signatureToken: token,
    signatureLinkValid: contract.signatureLinkValid ?? (contract.status === 'pending_signature' || contract.status === 'needs_revision' || contract.status === 'draft'),
    signatureEvidence: contract.signatureEvidence ?? null,
  };
}

const TOKEN_BY_ID: Record<number, string> = {
  1: 'sig-a7f3b2c1d4e5f6a8b9c0d1e2f3a4b5c6',
  2: 'sig-b8c4d3e5f6a7b9c0d1e2f3a4b5c6d7e8',
  3: 'sig-c9d5e4f6a7b8c0d1e2f3a4b5c6d7e8f9',
  4: 'sig-d0e6f5a7b8c9d1e2f3a4b5c6d7e8f9a0',
  5: 'sig-e1f7a6b8c9d0e2f3a4b5c6d7e8f9a0b1',
  6: 'sig-f2a8b7c9d0e1f3a4b5c6d7e8f9a0b1c2',
  7: 'sig-a3b9c8d0e1f2a4b5c6d7e8f9a0b1c2d3',
  8: 'sig-b4c0d9e1f2a3b5c6d7e8f9a0b1c2d3e4',
  9: 'sig-c5d1e0f2a3b4c6d7e8f9a0b1c2d3e4f5',
  10: 'sig-d6e2f1a3b4c5d7e8f9a0b1c2d3e4f5a6',
  11: 'sig-e7f3a2b4c5d6e8f9a0b1c2d3e4f5a6b7',
  12: 'sig-f8a4b3c5d6e7f9a0b1c2d3e4f5a6b7c8',
  13: 'sig-a9b5c4d6e7f8a0b1c2d3e4f5a6b7c8d9',
  14: 'sig-b0c6d5e7f8a9b1c2d3e4f5a6b7c8d9e0',
  15: 'sig-c1d7e6f8a9b0c2d3e4f5a6b7c8d9e0f1',
  16: 'sig-d2e8f7a9b0c1d3e4f5a6b7c8d9e0f1a2',
};

export function getSignatureToken(contractId: number): string | undefined {
  return TOKEN_BY_ID[contractId];
}